import * as React from 'react';
import TabContent from 'Components/TabContent';
import { convertTreatmentEfficiencyToRatingFormat, isPrimarySymptom } from 'Utility/ninmtUtil';
import PatientsRating from '../PatientsRating';
import { styled } from '@mui/system';
import colors from '../../../../../../../config/theme/colors';
import { RTMSContext } from 'Routes/Rtms/Document';
import { nowPartialDate, partialDateToValue, sortPartialDate } from 'neuro-utils';

const EmptyTabContent = styled('div')({
  margin: '0rem 0rem 0rem 0rem',
  padding: '2rem 2rem 2rem 2rem',
  marginRight: '0rem',
  border: `1px solid ${colors.gray}`,
  backgroundColor: colors.white,
});

const PatientsRatings = ({
  editIndex,
  editingEvent = true,
  formData,
  fm,
  symptomsAndLocations,
  sessionDate,
  initializePatientsRating,
}: {
  editIndex: number;
  editingEvent?: boolean;
  formData: IFormData<IRTMS>;
  fm: (m: string) => string;
  symptomsAndLocations: Array<ISymptomsAndLocations>;
  sessionDate?: PartialDate;
  initializePatientsRating?: (index: number, date?: PartialDate) => void;
}): React.JSX.Element => {
  const rtmsContext = React.useContext(RTMSContext);
  const { sortedAndMergedMyDocuments, patientMySQStatus, ninmtTreatmentPeriods } = rtmsContext;

  const treatmentEfficiencyDocs = sortedAndMergedMyDocuments.filter(
    d => d._type === 'treatmentEfficiency' && partialDateToValue(d.date) === partialDateToValue(sessionDate),
  ) as ITreatmentEfficiency[];

  const before = (treatmentEfficiencyDocs ?? []).find(d => d.timing === 'before');
  const after = (treatmentEfficiencyDocs ?? []).find(d => d.timing === 'after');

  const { document, onChange } = formData;

  // Current treatment period id is used to find primary symptom
  const treatmentPeriodsSorted = (ninmtTreatmentPeriods as Array<ININMTTreatmentPeriod & IControlProps>).sort((a, b) =>
    sortPartialDate(b.date, a.date),
  );
  const currentTreatmentPeriodId = treatmentPeriodsSorted.find(
    tp => partialDateToValue(tp.date) <= partialDateToValue(document.date),
  )?._id;

  const { date, patientsRating } = document.sessions?.[editIndex] ?? {};

  // For treatment efficiency values
  const [treatmentEfficiencyRating, setTreatmentEfficiencyRating] = React.useState<Array<IRTMSRating> | undefined>(
    undefined,
  );

  // Initialize/update treatment efficiency values
  React.useEffect(() => {
    if (!patientsRating) {
      if (!patientMySQStatus && editingEvent && initializePatientsRating) {
        initializePatientsRating(editIndex);
        return;
      }
      if (before || after) {
        setTreatmentEfficiencyRating(
          convertTreatmentEfficiencyToRatingFormat(before && after ? [before, after] : before ? before : after!),
        );
        return;
      }
      if (date && partialDateToValue(date) !== partialDateToValue(nowPartialDate()) && initializePatientsRating) {
        initializePatientsRating(editIndex);
      }
    }
  }, [sessionDate, symptomsAndLocations]);

  let usedRating =
    Array.isArray(patientsRating) && patientsRating.filter(s => s).length > 0
      ? patientsRating
      : treatmentEfficiencyRating
        ? treatmentEfficiencyRating
        : [];

  const ratingWithPrimarySymptom = usedRating.find(r =>
    r.ratingByLocation?.some(rl =>
      isPrimarySymptom(
        currentTreatmentPeriodId,
        symptomsAndLocations,
        r.symptom,
        r.symptom === 'other' ? rl.description : rl.location,
      ),
    ),
  );

  // If primary symptom is found, put its tab first in order
  if (ratingWithPrimarySymptom) {
    usedRating = [ratingWithPrimarySymptom, ...usedRating.filter(r => r !== ratingWithPrimarySymptom)];
  }

  // onChange for individual patient's ratings
  const patientsRatingOnChange =
    (index: number) =>
    (value: TOnChangeValues): void => {
      // Check if there exists an patient's rating with given index
      if (Array.isArray(patientsRating) && patientsRating.length >= index) {
        const newSessions = JSON.parse(JSON.stringify(document.sessions));
        const newPatientsRating = JSON.parse(JSON.stringify(patientsRating));
        newPatientsRating[index] = Object.assign({}, newPatientsRating[index], value);
        newSessions[editIndex].patientsRating = newPatientsRating;
        onChange && onChange({ sessions: newSessions });
      }
    };

  return Array.isArray(usedRating) && usedRating.length > 0 ? (
    <TabContent>
      {
        usedRating?.map((s: IRTMSRating, i: number) => ({
          key: `${s.id}${i}`,
          id: `${s.id}Tab${i}`,
          title: s.symptom ? fm(`rtms.patientsRating.opts.${s.symptom}`) : fm('rtms.patientsRating.symptomPlaceholder'),
          content: (
            <PatientsRating
              document={s}
              onChange={patientsRatingOnChange(i)}
              editingEvent={!patientsRating ? false : editingEvent}
              currentTreatmentPeriodId={currentTreatmentPeriodId}
            />
          ),
          count: i,
        })) as []
      }
    </TabContent>
  ) : (
    <EmptyTabContent>
      {fm(
        !patientsRating && !treatmentEfficiencyRating ? 'general.noData' : 'rtms.patientsRating.noSymptomsAndLocations',
      )}
    </EmptyTabContent>
  );
};

export default PatientsRatings;
